function morphClose(seed: Uint8Array, width: number, height: number) {
  const dilated = new Uint8Array(seed.length);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let active = 0;
      for (let dy = -1; dy <= 1 && !active; dy += 1) {
        const py = y + dy;
        if (py < 0 || py >= height) continue;
        for (let dx = -1; dx <= 1; dx += 1) {
          const px = x + dx;
          if (px >= 0 && px < width && seed[py * width + px]) {
            active = 1;
            break;
          }
        }
      }
      dilated[y * width + x] = active;
    }
  }

  const closed = new Uint8Array(seed.length);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let active = 1;
      for (let dy = -1; dy <= 1 && active; dy += 1) {
        const py = y + dy;
        if (py < 0 || py >= height) {
          active = 0;
          break;
        }
        for (let dx = -1; dx <= 1; dx += 1) {
          const px = x + dx;
          if (px < 0 || px >= width || !dilated[py * width + px]) {
            active = 0;
            break;
          }
        }
      }
      closed[y * width + x] = active;
    }
  }
  return closed;
}

type Component = {
  id: number;
  area: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
};

function connectedComponents(mask: Uint8Array, width: number, height: number) {
  const labels = new Int32Array(mask.length);
  const queue = new Int32Array(mask.length);
  const components: Component[] = [];
  let nextId = 0;

  for (let start = 0; start < mask.length; start += 1) {
    if (!mask[start] || labels[start]) continue;
    nextId += 1;
    let head = 0;
    let tail = 0;
    let left = width;
    let top = height;
    let right = 0;
    let bottom = 0;
    labels[start] = nextId;
    queue[tail++] = start;
    while (head < tail) {
      const index = queue[head++];
      const x = index % width;
      const y = Math.floor(index / width);
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x + 1);
      bottom = Math.max(bottom, y + 1);
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          if (!dx && !dy) continue;
          const px = x + dx;
          const py = y + dy;
          if (px < 0 || py < 0 || px >= width || py >= height) continue;
          const neighbor = py * width + px;
          if (!mask[neighbor] || labels[neighbor]) continue;
          labels[neighbor] = nextId;
          queue[tail++] = neighbor;
        }
      }
    }
    components.push({ id: nextId, area: tail, left, top, right, bottom });
  }
  return { labels, components };
}

function fillHoles(mask: Uint8Array, width: number, height: number) {
  const outside = new Uint8Array(mask.length);
  const queue = new Int32Array(mask.length);
  let head = 0;
  let tail = 0;
  const visit = (index: number) => {
    if (outside[index] || mask[index]) return;
    outside[index] = 1;
    queue[tail++] = index;
  };
  for (let x = 0; x < width; x += 1) {
    visit(x);
    visit((height - 1) * width + x);
  }
  for (let y = 1; y < height - 1; y += 1) {
    visit(y * width);
    visit(y * width + width - 1);
  }
  while (head < tail) {
    const index = queue[head++];
    const x = index % width;
    const y = Math.floor(index / width);
    if (x > 0) visit(index - 1);
    if (x + 1 < width) visit(index + 1);
    if (y > 0) visit(index - width);
    if (y + 1 < height) visit(index + width);
  }
  const filled = new Uint8Array(mask.length);
  for (let index = 0; index < mask.length; index += 1) {
    filled[index] = mask[index] || !outside[index] ? 1 : 0;
  }
  return filled;
}

export function finishAlphaMatte(alpha: Uint8ClampedArray, width: number, height: number) {
  const histogram = new Uint32Array(256);
  for (const value of alpha) histogram[value] += 1;
  const target = Math.max(1, Math.floor(alpha.length * 0.98));
  let cumulative = 0;
  let percentile98 = 0;
  for (let value = 0; value < histogram.length; value += 1) {
    cumulative += histogram[value];
    if (cumulative >= target) {
      percentile98 = value;
      break;
    }
  }
  const threshold = Math.max(2, Math.min(16, percentile98 * 0.13));
  const seed = new Uint8Array(alpha.length);
  for (let index = 0; index < alpha.length; index += 1) {
    seed[index] = alpha[index] > threshold ? 1 : 0;
  }
  const closed = morphClose(seed, width, height);
  const { labels, components } = connectedComponents(closed, width, height);
  const largest = components.reduce<Component | null>(
    (best, component) => !best || component.area > best.area ? component : best,
    null,
  );
  if (!largest) return new Uint8ClampedArray(alpha.length);

  const selected = new Set<number>([largest.id]);
  const paddingX = width * 0.14;
  const paddingY = height * 0.18;
  for (const component of components) {
    if (component.id === largest.id || component.area < Math.max(8, largest.area * 0.012)) continue;
    const nearby = component.right >= largest.left - paddingX
      && component.left <= largest.right + paddingX
      && component.bottom >= largest.top - paddingY
      && component.top <= largest.bottom + paddingY;
    if (nearby) selected.add(component.id);
  }

  const silhouette = new Uint8Array(alpha.length);
  for (let index = 0; index < labels.length; index += 1) {
    silhouette[index] = selected.has(labels[index]) ? 1 : 0;
  }
  const filled = fillHoles(silhouette, width, height);
  const output = new Uint8ClampedArray(alpha.length);
  for (let index = 0; index < output.length; index += 1) {
    output[index] = filled[index] ? 255 : 0;
  }
  return output;
}
